/**
 * SpatialBroadcastCard.tsx
 *
 * The thing a user sees when they walk near a talking building.
 *
 * Two states, one component. Before the user opts in it is a strip —
 * name, distance, a Listen button — because a broadcast being available
 * is not a reason to seize the screen. After they tap Listen it becomes
 * the compact player, mounted in place, so connecting does not move the
 * control they just pressed.
 *
 * Objects that are configured to speak but currently silent are shown
 * too, dimmed. "This place can talk to you" is worth knowing and is not
 * the same claim as "this place is talking".
 */

import React, { useState } from 'react';

import { SpatialBroadcastPlayer } from './SpatialBroadcastPlayer';
import type { NearbySpatialBroadcast } from '../../services/spatial/spatialPresenceClient';

export interface SpatialBroadcastCardProps {
  broadcast: NearbySpatialBroadcast;
  viewerWallet?: string | null;
  country?: number | null;
  /** Open immediately — used when media config asks to surface the player. */
  defaultOpen?: boolean;
  onOpen?: (objectId: string) => void;
  onClose?: (objectId: string) => void;
}

const STYLE_ID = 'gt-spatial-card-styles';

function installStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .gt-sbc {
      display: flex; align-items: center; gap: 10px;
      padding: 9px 12px; border-radius: 12px;
      background: rgba(14, 16, 22, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.10);
      color: #e9edf5; font-size: 12px; max-width: 320px;
    }
    .gt-sbc--quiet { opacity: 0.72; }
    .gt-sbc__body { flex: 1 1 auto; min-width: 0; }
    .gt-sbc__title {
      font-weight: 600; overflow: hidden;
      text-overflow: ellipsis; white-space: nowrap;
    }
    .gt-sbc__sub { color: rgba(233, 237, 245, 0.6); font-size: 11px; margin-top: 1px; }
    .gt-sbc__live {
      display: inline-flex; align-items: center; gap: 5px;
      font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: #ff5470; margin-right: 6px;
    }
    .gt-sbc__dot { width: 7px; height: 7px; border-radius: 50%; background: #ff5470; }
    .gt-sbc__btn {
      appearance: none; border: 1px solid #ff5470; background: #ff5470;
      color: #10121a; border-radius: 8px; padding: 6px 12px;
      font-size: 11px; font-weight: 700; cursor: pointer; flex: 0 0 auto;
    }
    .gt-sbc__btn:hover { background: #ff6c84; }
    .gt-sbc__hint { color: rgba(233, 237, 245, 0.45); font-size: 11px; flex: 0 0 auto; }
    @media (max-width: 480px) { .gt-sbc { max-width: none; width: 100%; } }
  `;
  document.head.appendChild(s);
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '';
  return meters < 1000 ? `${Math.round(meters)} m away` : `${(meters / 1000).toFixed(1)} km away`;
}

export const SpatialBroadcastCard: React.FC<SpatialBroadcastCardProps> = ({
  broadcast,
  viewerWallet = null,
  country = null,
  defaultOpen = false,
  onOpen,
  onClose,
}) => {
  installStyles();

  const isLive = !!broadcast.live;
  const [open, setOpen] = useState(defaultOpen && isLive);

  if (open && broadcast.live) {
    return (
      <SpatialBroadcastPlayer
        spatialObjectId={broadcast.spatialObjectId}
        broadcasterId={broadcast.live.broadcasterId}
        objectName={broadcast.name}
        title={broadcast.live.title}
        distanceMeters={broadcast.distanceMeters}
        volume={broadcast.volume}
        autoPlayAudio={broadcast.autoPlayAudio}
        viewerWallet={viewerWallet}
        country={country}
        onClose={() => {
          setOpen(false);
          onClose?.(broadcast.spatialObjectId);
        }}
      />
    );
  }

  return (
    <div
      className={`gt-sbc${isLive ? '' : ' gt-sbc--quiet'}`}
      data-object-id={broadcast.spatialObjectId}
    >
      <div className="gt-sbc__body">
        <div className="gt-sbc__title">
          {isLive && (
            <span className="gt-sbc__live">
              <span className="gt-sbc__dot" />
              Live
            </span>
          )}
          {broadcast.live?.title || broadcast.name || 'Spatial object'}
        </div>
        <div className="gt-sbc__sub">
          {formatDistance(broadcast.distanceMeters)}
          {isLive
            ? ''
            : broadcast.agentEnabled
              ? ' · can speak when something happens here'
              : ' · not broadcasting'}
          {broadcast.placement?.zone?.name && ` · ${broadcast.placement.zone.name}`}
          {broadcast.placement?.floor?.name && ` · ${broadcast.placement.floor.name}`}
          {(broadcast.altitude?.confidence === 'low'
            || broadcast.placement?.floorConfidence === 'low') && ' · floor uncertain'}
        </div>
      </div>

      {isLive ? (
        <button
          type="button"
          className="gt-sbc__btn"
          onClick={() => {
            setOpen(true);
            onOpen?.(broadcast.spatialObjectId);
          }}
        >
          Listen
        </button>
      ) : (
        <span className="gt-sbc__hint">Quiet</span>
      )}
    </div>
  );
};

export default SpatialBroadcastCard;
